import { useEffect, useLayoutEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared by Modal and Popover — calls `onEscape` while `active` is true.
 *
 * Subscribes in a layout effect, not a passive one: a passive effect runs
 * after the browser has painted, so the overlay is on screen for a frame (far
 * longer under load) with no listener attached, and an Escape pressed in that
 * window is dropped and the overlay never closes.
 */
export function useEscapeKey(active: boolean, onEscape: () => void) {
    useLayoutEffect(() => {
        if (!active) return;

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') onEscape();
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [active, onEscape]);
}

type ElementRef = RefObject<HTMLElement | null>;

/**
 * Shared by Popover and PlanetSwitcher — calls `onOutside` on a pointerdown
 * outside every mounted element in `refs`. Pass several when more than one
 * element counts as inside, e.g. a trigger button and the list it portals.
 * With nothing mounted it never fires. Memoize an array argument: a new array
 * each render re-subscribes the listener.
 */
export function useOutsidePointerDown(active: boolean, refs: ElementRef | ElementRef[], onOutside: () => void) {
    // Layout effect for the same reason as `useEscapeKey`: the listener must
    // be live from the moment the overlay is painted.
    useLayoutEffect(() => {
        if (!active) return;
        const list = Array.isArray(refs) ? refs : [refs];

        function handlePointerDown(event: PointerEvent) {
            const mounted = list.map((ref) => ref.current).filter((el): el is HTMLElement => el !== null);
            if (mounted.length > 0 && !mounted.some((el) => el.contains(event.target as Node))) onOutside();
        }

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [active, refs, onOutside]);
}

/**
 * Traps Tab focus within `containerRef` while `active`, and restores focus
 * to whatever was focused beforehand once it becomes inactive again.
 */
export function useFocusTrap(active: boolean, containerRef: RefObject<HTMLElement | null>) {
    useEffect(() => {
        if (!active) return;

        const previouslyFocused = document.activeElement as HTMLElement | null;
        const container = containerRef.current;
        const firstFocusable = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (firstFocusable ?? container)?.focus();

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key !== 'Tab' || !container) return;

            const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }

            const first = focusable[0]!;
            const last = focusable[focusable.length - 1]!;

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus();
        };
    }, [active, containerRef]);
}

/** Locks body scroll while `active`, restoring the previous overflow on cleanup. */
export function useBodyScrollLock(active: boolean) {
    useEffect(() => {
        if (!active) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [active]);
}
